import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';
import api from '../../services/api';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [masterData, setMasterData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Image upload states
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  const [galleryImage, setGalleryImage] = useState(null);
  const [galleryImagePreview, setGalleryImagePreview] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(null);
  
  // Cascading dropdown states
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [workingAsOptions, setWorkingAsOptions] = useState([]);
  const [workingWithOptions, setWorkingWithOptions] = useState([]);
  
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [loadingWorkingAs, setLoadingWorkingAs] = useState(false);
  const [loadingWorkingWith, setLoadingWorkingWith] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  const fetchMasterData = async () => {
    try {
      const response = await settingsAPI.getMasterData();
      setMasterData(response.data.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getUserDetails(id);
      setUser(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch user details');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  // Profile picture handlers
  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return;
    
    setUploadingProfilePicture(true);
    try {
      const formData = new FormData();
      formData.append('profile_photo', profilePicture);
      const response = await api.post(`/users/${id}/upload-profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success('Profile picture updated successfully!');
        setProfileDialogOpen(false);
        setProfilePicture(null);
        setProfilePicturePreview(null);
        fetchUserDetails();
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  // Gallery image handlers
  const handleGalleryImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setGalleryImage(file);
      setGalleryImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadGalleryImage = async () => {
    if (!galleryImage) return;
    
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('gallery_image', galleryImage);
      const response = await api.post(`/users/${id}/upload-gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success('Gallery image added successfully!');
        setGalleryDialogOpen(false);
        setGalleryImage(null);
        setGalleryImagePreview(null);
        fetchUserDetails();
      }
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      toast.error('Failed to upload gallery image');
    } finally {
      setUploadingGallery(false);
    }
  };

  const deleteGalleryImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        const response = await api.delete(`/users/${id}/gallery/${imageId}`);
        if (response.data.success) {
          toast.success('Image deleted successfully');
          fetchUserDetails();
        }
      } catch (error) {
        console.error('Error deleting gallery image:', error);
        toast.error('Failed to delete image');
      }
    }
  };

  // Load states when country changes
  const loadStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    try {
      const response = await settingsAPI.getStates(countryId);
      setStates(response.data.data);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  // Load cities when state changes
  const loadCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const response = await settingsAPI.getCities(stateId);
      setCities(response.data.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  // Load communities when religion changes
  const loadCommunities = async (religionId) => {
    if (!religionId) {
      setCommunities([]);
      return;
    }
    setLoadingCommunities(true);
    try {
      const response = await settingsAPI.getCommunitiesByReligion(religionId);
      setCommunities(response.data.data);
    } catch (error) {
      console.error('Error loading communities:', error);
    } finally {
      setLoadingCommunities(false);
    }
  };

  // Load qualifications by category
  const loadQualificationsByCategory = async (categoryId) => {
    if (!categoryId) {
      setQualifications([]);
      return;
    }
    setLoadingQualifications(true);
    try {
      const filteredQualifications = (masterData.qualifications || []).filter(
        q => q.category_id == categoryId
      );
      setQualifications(filteredQualifications);
    } catch (error) {
      console.error('Error loading qualifications:', error);
    } finally {
      setLoadingQualifications(false);
    }
  };

  // Load working as by category
  const loadWorkingAsByCategory = async (categoryId) => {
    if (!categoryId) {
      setWorkingAsOptions([]);
      return;
    }
    setLoadingWorkingAs(true);
    try {
      const filteredWorkingAs = (masterData.workingAs || []).filter(
        w => w.category_id == categoryId
      );
      setWorkingAsOptions(filteredWorkingAs);
    } catch (error) {
      console.error('Error loading working as:', error);
    } finally {
      setLoadingWorkingAs(false);
    }
  };

  // Load working with by category
  const loadWorkingWithByCategory = async (categoryId) => {
    if (!categoryId) {
      setWorkingWithOptions([]);
      return;
    }
    setLoadingWorkingWith(true);
    try {
      const filteredWorkingWith = (masterData.workingWith || []).filter(
        w => w.category_id == categoryId
      );
      setWorkingWithOptions(filteredWorkingWith);
    } catch (error) {
      console.error('Error loading working with:', error);
    } finally {
      setLoadingWorkingWith(false);
    }
  };

  const handleEdit = (section, data) => {
    setEditingSection(section);
    setEditData({ ...data });
    
    // Load cascading data based on section
    if (section === 'contact') {
      if (data.country_id) {
        loadStates(data.country_id);
        if (data.state_id) {
          loadCities(data.state_id);
        }
      }
    }
    if (section === 'spiritual') {
      if (data.religion_id) {
        loadCommunities(data.religion_id);
      }
    }
    if (section === 'education') {
      if (data.qualification_category_id) {
        loadQualificationsByCategory(data.qualification_category_id);
      }
    }
    if (section === 'profession') {
      if (data.working_as_category_id) {
        loadWorkingAsByCategory(data.working_as_category_id);
      }
      if (data.working_with_category_id) {
        loadWorkingWithByCategory(data.working_with_category_id);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditData({});
    setStates([]);
    setCities([]);
    setCommunities([]);
    setQualifications([]);
    setWorkingAsOptions([]);
    setWorkingWithOptions([]);
  };

  const handleSave = async (section, updatedData) => {
    setSaving(true);
    try {
      let response;
      const userId = id;
      
      switch(section) {
        case 'basic':
          const formattedData = {
            first_name: updatedData.first_name,
            last_name: updatedData.last_name,
            phone: updatedData.phone,
            gender: updatedData.gender,
            date_of_birth: updatedData.date_of_birth ? moment(updatedData.date_of_birth).format('YYYY-MM-DD') : null,
            about_me: updatedData.about_me
          };
          response = await api.put(`/users/${userId}/basic`, formattedData);
          break;
          
        case 'contact':
          response = await api.put(`/users/${userId}/contact`, {
            marital_status_id: updatedData.marital_status_id,
            gotra_id: updatedData.gotra_id,
            country_id: updatedData.country_id,
            state_id: updatedData.state_id,
            city_id: updatedData.city_id,
            residency_status_id: updatedData.residency_status_id
          });
          break;
          
        case 'spiritual':
          response = await api.put(`/users/${userId}/spiritual`, {
            religion_id: updatedData.religion_id,
            community_id: updatedData.community_id,
            height_id: updatedData.height_id,
            weight_id: updatedData.weight_id,
            diet_id: updatedData.diet_id,
            manglik: updatedData.manglik === 'yes' ? 1 : (updatedData.manglik === 'no' ? 0 : null)
          });
          break;
          
        case 'education':
          response = await api.put(`/users/${userId}/education`, {
            qualification_category_id: updatedData.qualification_category_id,
            qualification_id: updatedData.qualification_id,
            college_name: updatedData.college_name
          });
          break;
          
        case 'profession':
          response = await api.put(`/users/${userId}/profession`, {
            working_as_category_id: updatedData.working_as_category_id,
            working_as_id: updatedData.working_as_id,
            working_with_category_id: updatedData.working_with_category_id,
            working_with_id: updatedData.working_with_id,
            company_name: updatedData.company_name
          });
          break;
          
        case 'family_details':
          response = await api.put(`/users/${userId}/family-details`, {
            you_have_id: updatedData.you_have_id,
            family_type_id: updatedData.family_type_id,
            manglik: updatedData.manglik === 'yes' ? 1 : (updatedData.manglik === 'no' ? 0 : null),
            sun_sign_id: updatedData.sun_sign_id,
            moon_sign_id: updatedData.moon_sign_id
          });
          break;
          
        case 'partner_preference':
          response = await api.put(`/users/${userId}/partner-preference`, {
            age_from: updatedData.age_from,
            age_to: updatedData.age_to,
            height_from_id: updatedData.height_from_id,
            height_to_id: updatedData.height_to_id,
            manglik: updatedData.manglik === '1' ? 1 : (updatedData.manglik === '0' ? 0 : null),
            marital_status_ids: updatedData.marital_status_ids,
            religion_ids: updatedData.religion_ids,
            country_ids: updatedData.country_ids,
            qualification_ids: updatedData.qualification_ids,
            working_as_ids: updatedData.working_as_ids,
            diet_ids: updatedData.diet_ids
          });
          break;
          
        default:
          break;
      }
      
      if (response && response.data.success) {
        setSnackbar({
          open: true,
          message: `${section} information updated successfully!`,
          severity: 'success'
        });
        setEditingSection(null);
        await fetchUserDetails();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderEditableField = (field, data, type = 'text', options = []) => {
    const fieldKey = field.key;
    const currentValue = editData[fieldKey] !== undefined ? editData[fieldKey] : data[fieldKey];
    
    if (type === 'date' && currentValue) {
      return (
        <TextField
          fullWidth
          size="small"
          label={field.label}
          type="date"
          value={moment(currentValue).format('YYYY-MM-DD')}
          onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      );
    }
    
    switch (type) {
      case 'multi-select':
        const selectedValues = currentValue ? String(currentValue).split(',').filter(v => v) : [];
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              value={selectedValues}
              label={field.label}
              onChange={(e) => {
                const value = e.target.value;
                setEditData({ ...editData, [fieldKey]: value.join(',') });
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = options.find(opt => String(opt.id) === String(value));
                    return (
                      <Chip key={value} label={option?.name || value} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  <Checkbox checked={selectedValues.indexOf(String(option.id)) > -1} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        
      case 'select':
        // Country dropdown
        if (fieldKey === 'country_id') {
          const countryOptions = masterData.countries || [];
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, state_id: '', city_id: '' });
                  loadStates(newValue);
                }}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {countryOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // State dropdown
        if (fieldKey === 'state_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, city_id: '' });
                  loadCities(newValue);
                }}
                label={field.label}
                disabled={!editData.country_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingStates ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  states.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // City dropdown
        if (fieldKey === 'city_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
                disabled={!editData.state_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingCities ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  cities.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // Religion dropdown
        if (fieldKey === 'religion_id') {
          const religionOptions = masterData.religions || [];
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, community_id: '' });
                  loadCommunities(newValue);
                }}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {religionOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Community dropdown
        if (fieldKey === 'community_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
                disabled={!editData.religion_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingCommunities ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  communities.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // Qualification Category dropdown
        if (fieldKey === 'qualification_category_id') {
          const categoryOptions = masterData.qualificationCategories || [];
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, qualification_id: '' });
                  loadQualificationsByCategory(newValue);
                }}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {categoryOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Qualification dropdown
        if (fieldKey === 'qualification_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
                disabled={!editData.qualification_category_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingQualifications ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  qualifications.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // Working As Category dropdown
        if (fieldKey === 'working_as_category_id') {
          const categoryOptions = masterData.workingAsCategories || [];
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, working_as_id: '' });
                  loadWorkingAsByCategory(newValue);
                }}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {categoryOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Working As dropdown
        if (fieldKey === 'working_as_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
                disabled={!editData.working_as_category_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingWorkingAs ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  workingAsOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // Working With Category dropdown
        if (fieldKey === 'working_with_category_id') {
          const categoryOptions = masterData.workingWithCategories || [];
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditData({ ...editData, [fieldKey]: newValue, working_with_id: '' });
                  loadWorkingWithByCategory(newValue);
                }}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {categoryOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Working With dropdown
        if (fieldKey === 'working_with_id') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
                disabled={!editData.working_with_category_id}
              >
                <MenuItem value="">None</MenuItem>
                {loadingWorkingWith ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  workingWithOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }
        
        // Height dropdown
        if (fieldKey === 'height_id') {
          const heightOptions = (masterData.heights || []).map(h => ({
            id: h.id,
            name: h.height_ft_inch ? `${h.height_cm} cm (${h.height_ft_inch})` : `${h.height_cm} cm`
          }));
          
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {heightOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Weight dropdown
        if (fieldKey === 'weight_id') {
          const weightOptions = (masterData.weights || []).map(w => ({
            id: w.id,
            name: `${w.weight_kg} kg`
          }));
          
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={currentValue || ''}
                onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
                label={field.label}
              >
                <MenuItem value="">None</MenuItem>
                {weightOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        
        // Regular select dropdown
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={currentValue || ''}
              onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
              label={field.label}
            >
              <MenuItem value="">None</MenuItem>
              {options && options.length > 0 ? (
                options.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name || opt.status_name || opt.gotra_name || opt.diet_name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No options available</MenuItem>
              )}
            </Select>
          </FormControl>
        );
        
      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            multiline
            rows={3}
            value={currentValue || ''}
            onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
          />
        );
        
      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type={type}
            value={currentValue || ''}
            onChange={(e) => setEditData({ ...editData, [fieldKey]: e.target.value })}
          />
        );
    }
  };

  const renderSection = (title, section, data, fields) => {
    const isEditing = editingSection === section;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ color: '#e53935' }}>
              {title}
            </Typography>
            {!isEditing ? (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleEdit(section, data)}
                sx={{ color: '#1e88e5' }}
                disabled={saving}
              >
                Edit
              </Button>
            ) : (
              <Box>
                <Button
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSave(section, editData)}
                  sx={{ color: '#4caf50', mr: 1 }}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  sx={{ color: '#f44336' }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {isEditing ? (
            <Grid container spacing={2}>
              {fields.map((field) => (
                <Grid item xs={12} sm={field.sm || 6} md={field.md || 4} key={field.key}>
                  {renderEditableField(field, data, field.type, field.options)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {fields.map((field) => {
                let displayValue = '';
                
                // Handle height display
                if (field.key === 'height_id' && data[field.key]) {
                  const heightObj = (masterData.heights || []).find(h => h.id == data[field.key]);
                  if (heightObj) {
                    displayValue = heightObj.height_ft_inch ? 
                      `${heightObj.height_cm} cm (${heightObj.height_ft_inch})` : 
                      `${heightObj.height_cm} cm`;
                  } else {
                    displayValue = data[field.key];
                  }
                }
                // Handle partner preference display with names
                else if (field.displayKey && data[field.displayKey]) {
                  displayValue = data[field.displayKey];
                } 
                else if (field.type === 'multi-select' && data[field.key]) {
                  const selectedIds = String(data[field.key]).split(',').filter(v => v);
                  const selectedNames = selectedIds.map(id => {
                    const option = field.options.find(opt => String(opt.id) === String(id));
                    return option?.name || id;
                  }).join(', ');
                  displayValue = selectedNames || 'N/A';
                }
                // Handle weight display
                else if (field.key === 'weight_id' && data[field.key]) {
                  const weightObj = (masterData.weights || []).find(w => w.id == data[field.key]);
                  if (weightObj) {
                    displayValue = `${weightObj.weight_kg} kg`;
                  } else {
                    displayValue = data[field.key];
                  }
                }
                // Handle fields with displayKey
                else if (field.displayKey && data[field.displayKey]) {
                  displayValue = data[field.displayKey];
                } else if (data[field.key]) {
                  displayValue = data[field.key];
                }
                
                if (field.type === 'date' && displayValue) {
                  displayValue = moment(displayValue).format('YYYY-MM-DD');
                }
                
                return (
                  <Grid item xs={12} sm={field.sm || 6} md={field.md || 4} key={field.key}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {field.label}
                    </Typography>
                    <Typography variant="body1">
                      {field.format ? field.format(displayValue) : (displayValue || 'N/A')}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  const calculateAge = (dob) => {
    return dob ? moment().diff(moment(dob), 'years') : 'N/A';
  };

  const sections = {
    basic: {
      title: 'Basic Information',
      data: user?.basic || {},
      fields: [
        { key: 'first_name', label: 'First Name', sm: 6 },
        { key: 'last_name', label: 'Last Name', sm: 6 },
        { key: 'phone', label: 'Phone Number', sm: 6 },
        { key: 'gender', label: 'Gender', sm: 6, type: 'select', options: [
          { id: 'male', name: 'Male' },
          { id: 'female', name: 'Female' },
          { id: 'other', name: 'Other' }
        ]},
        { key: 'date_of_birth', label: 'Date of Birth', sm: 6, type: 'date', format: (val) => val ? moment(val).format('YYYY-MM-DD') : 'N/A' },
        { key: 'about_me', label: 'About Me', md: 12, type: 'textarea' }
      ]
    },
    contact: {
      title: 'Contact & Family Information',
      data: user?.contact || {},
      fields: [
        { key: 'marital_status_id', label: 'Marital Status', displayKey: 'marital_status_name', sm: 6, type: 'select', options: masterData.maritalStatus || [] },
        { key: 'gotra_id', label: 'Gotra', displayKey: 'gotra_name', sm: 6, type: 'select', options: masterData.gotras || [] },
        { key: 'country_id', label: 'Country', displayKey: 'country_name', sm: 6, type: 'select', options: masterData.countries || [] },
        { key: 'state_id', label: 'State', displayKey: 'state_name', sm: 6, type: 'select', options: [] },
        { key: 'city_id', label: 'City', displayKey: 'city_name', sm: 6, type: 'select', options: [] },
        { key: 'residency_status_id', label: 'Residency Status', displayKey: 'residency_status_name', sm: 6, type: 'select', options: masterData.residencyStatus || [] }
      ]
    },
    family_details: {
      title: 'Family Details',
      data: user?.family_details || {},
      fields: [
        { key: 'you_have_id', label: 'You Have', displayKey: 'you_have_name', sm: 6, type: 'select', options: masterData.youHaveOptions || [] },
        { key: 'family_type_id', label: 'Family Type', displayKey: 'family_type_name', sm: 6, type: 'select', options: masterData.familyTypes || [] },
        { key: 'manglik', label: 'Manglik', sm: 6, type: 'select', options: [
          { id: 'yes', name: 'Yes' },
          { id: 'no', name: 'No' }
        ]},
        { key: 'sun_sign_id', label: 'Sun Sign', displayKey: 'sun_sign_name', sm: 6, type: 'select', options: masterData.sunSigns || [] },
        { key: 'moon_sign_id', label: 'Moon Sign', displayKey: 'moon_sign_name', sm: 6, type: 'select', options: masterData.moonSigns || [] }
      ]
    },
    spiritual: {
      title: 'Spiritual Information',
      data: user?.spiritual || {},
      fields: [
        { key: 'religion_id', label: 'Religion', displayKey: 'religion_name', sm: 6, type: 'select', options: masterData.religions || [] },
        { key: 'community_id', label: 'Community', displayKey: 'community_name', sm: 6, type: 'select', options: [] },
        { key: 'height_id', label: 'Height', displayKey: 'height_cm', sm: 4, type: 'select', options: masterData.heights || [] },
        { key: 'weight_id', label: 'Weight', displayKey: 'weight_kg', sm: 4, type: 'select', options: masterData.weights || [] },
        { key: 'diet_id', label: 'Diet', displayKey: 'diet_name', sm: 4, type: 'select', options: masterData.diets || [] },
        { key: 'manglik', label: 'Manglik', sm: 6, type: 'select', options: [
          { id: 'yes', name: 'Yes' },
          { id: 'no', name: 'No' }
        ]}
      ]
    },
    partner_preference: {
      title: 'Partner Preference',
      data: user?.partner_preference || {},
      fields: [
        { key: 'age_from', label: 'Minimum Age', sm: 6, type: 'number' },
        { key: 'age_to', label: 'Maximum Age', sm: 6, type: 'number' },
        { key: 'height_from_id', label: 'Minimum Height', displayKey: 'height_from_display', sm: 6, type: 'select', options: masterData.heights || [] },
        { key: 'height_to_id', label: 'Maximum Height', displayKey: 'height_to_display', sm: 6, type: 'select', options: masterData.heights || [] },
        { key: 'manglik', label: 'Manglik Preference', sm: 6, type: 'select', options: [
          { id: '1', name: 'Yes' },
          { id: '0', name: 'No' },
          { id: '', name: 'Does not matter' }
        ]},
        { key: 'marital_status_ids', label: 'Preferred Marital Status', displayKey: 'marital_status_names', sm: 12, type: 'multi-select', options: masterData.maritalStatus || [] },
        { key: 'religion_ids', label: 'Preferred Religions', displayKey: 'religion_names', sm: 12, type: 'multi-select', options: masterData.religions || [] },
        { key: 'country_ids', label: 'Preferred Countries', displayKey: 'country_names', sm: 12, type: 'multi-select', options: masterData.countries || [] },
        { key: 'qualification_ids', label: 'Preferred Qualifications', displayKey: 'qualification_names', sm: 12, type: 'multi-select', options: masterData.qualifications || [] },
        { key: 'working_as_ids', label: 'Preferred Professions', displayKey: 'working_as_names', sm: 12, type: 'multi-select', options: masterData.workingAs || [] },
        { key: 'diet_ids', label: 'Preferred Diet', displayKey: 'diet_names', sm: 12, type: 'multi-select', options: masterData.diets || [] }
      ]
    },
    education: {
      title: 'Education',
      data: user?.wealth || {},
      fields: [
        { key: 'qualification_category_id', label: 'Qualification Category', displayKey: 'qualification_category_name', sm: 6, type: 'select', options: masterData.qualificationCategories || [] },
        { key: 'qualification_id', label: 'Qualification', displayKey: 'qualification_name', sm: 6, type: 'select', options: [] },
        { key: 'college_name', label: 'College/University', md: 12 }
      ]
    },
    profession: {
      title: 'Professional Information',
      data: user?.wealth || {},
      fields: [
        { key: 'working_as_category_id', label: 'Working As Category', displayKey: 'working_as_category_name', sm: 6, type: 'select', options: masterData.workingAsCategories || [] },
        { key: 'working_as_id', label: 'Working As', displayKey: 'working_as_name', sm: 6, type: 'select', options: [] },
        { key: 'working_with_category_id', label: 'Working With Category', displayKey: 'working_with_category_name', sm: 6, type: 'select', options: masterData.workingWithCategories || [] },
        { key: 'working_with_id', label: 'Working With', displayKey: 'working_with_name', sm: 6, type: 'select', options: [] },
        { key: 'company_name', label: 'Company Name', md: 12 }
      ]
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return null;

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/users')}
        sx={{ mb: 2, color: '#e53935' }}
      >
        Back to Users
      </Button>

      {/* Profile Header with Edit Option */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #e5393520 0%, #1e88e520 100%)' }}>
        <Box display="flex" alignItems="center" gap={3}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={user.basic?.profile_photo_url}
              sx={{ width: 100, height: 100, border: '3px solid #e53935' }}
            >
              {user.basic?.first_name?.charAt(0)}
            </Avatar>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: '#e53935',
                color: 'white',
                '&:hover': { backgroundColor: '#c62828' }
              }}
              onClick={() => setProfileDialogOpen(true)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold">
              {user.basic?.first_name} {user.basic?.last_name}
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              User ID: {user.basic?.id} | Phone: {user.basic?.phone}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip
                icon={user.basic?.is_verified ? <VerifiedIcon /> : null}
                label={user.basic?.is_verified ? 'Verified' : 'Unverified'}
                color={user.basic?.is_verified ? 'success' : 'warning'}
              />
              {user.basic?.is_blocked && (
                <Chip label="Blocked" color="error" />
              )}
              <Chip
                label={`Age: ${calculateAge(user.basic?.date_of_birth)} years`}
                variant="outlined"
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="h6" color="#1e88e5">
              Joined: {moment(user.basic?.created_at).format('YYYY-MM-DD')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Profile Picture Edit Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Profile Picture</Typography>
            <IconButton onClick={() => setProfileDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {profilePicturePreview ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar src={profilePicturePreview} sx={{ width: 150, height: 150, mx: 'auto', mb: 2 }} />
                <IconButton
                  sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white' }}
                  onClick={() => {
                    setProfilePicture(null);
                    setProfilePicturePreview(null);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            ) : (
              <Avatar
                src={user.basic?.profile_photo_url}
                sx={{ width: 150, height: 150, mx: 'auto', mb: 2, border: '2px solid #e53935' }}
              >
                {user.basic?.first_name?.charAt(0)}
              </Avatar>
            )}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2 }}
            >
              Choose New Image
              <input type="file" hidden accept="image/*" onChange={handleProfilePictureChange} />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Square image recommended, max 5MB. JPG, PNG or GIF
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={uploadProfilePicture}
            variant="contained"
            disabled={!profilePicture || uploadingProfilePicture}
            sx={{ background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)' }}
          >
            {uploadingProfilePicture ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Tabs */}
      <Paper>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root.Mui-selected': {
              color: '#e53935',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#e53935',
            },
          }}
        >
          <Tab label="Personal Info" />
          <Tab label="Contact & Family" />
          <Tab label="Family Details" />
          <Tab label="Spiritual" />
          <Tab label="Partner Preference" />
          <Tab label="Education" />
          <Tab label="Profession" />
          <Tab label="Documents & Photos" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderSection(sections.basic.title, 'basic', sections.basic.data, sections.basic.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderSection(sections.contact.title, 'contact', sections.contact.data, sections.contact.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderSection(sections.family_details.title, 'family_details', sections.family_details.data, sections.family_details.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {renderSection(sections.spiritual.title, 'spiritual', sections.spiritual.data, sections.spiritual.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {renderSection(sections.partner_preference.title, 'partner_preference', sections.partner_preference.data, sections.partner_preference.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          {renderSection(sections.education.title, 'education', sections.education.data, sections.education.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          {renderSection(sections.profession.title, 'profession', sections.profession.data, sections.profession.fields)}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: '#e53935' }}>
                  Document Verification
                </Typography>
              </Box>
              {user.documents && user.documents.length > 0 ? (
                user.documents.map((doc, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {doc.document_type_name || 'Document'}
                    </Typography>
                    <Typography variant="body2">Status: {doc.status}</Typography>
                    <Typography variant="body2">Submitted: {moment(doc.created_at).format('YYYY-MM-DD')}</Typography>
                    {doc.admin_notes && (
                      <Typography variant="body2" color="error">Notes: {doc.admin_notes}</Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography>No documents submitted</Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: '#e53935' }}>
                  Gallery Photos
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddPhotoIcon />}
                  onClick={() => {
                    setGalleryImage(null);
                    setGalleryImagePreview(null);
                    setGalleryDialogOpen(true);
                  }}
                  sx={{ borderColor: '#e53935', color: '#e53935' }}
                >
                  Add Photo
                </Button>
              </Box>
              <Grid container spacing={2}>
                {user.gallery && user.gallery.length > 0 ? (
                  user.gallery.map((photo, index) => (
                    <Grid item key={photo.id}>
                      <Box sx={{ position: 'relative' }}>
                        <img 
                          src={photo.image_url} 
                          alt="Gallery" 
                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                          onClick={() => window.open(photo.image_url, '_blank')}
                        />
                        <Chip 
                          label={photo.is_approved ? 'Approved' : 'Pending'}
                          size="small"
                          color={photo.is_approved ? 'success' : 'warning'}
                          sx={{ position: 'absolute', bottom: 5, right: 5 }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                          }}
                          onClick={() => deleteGalleryImage(photo.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography>No gallery photos</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Gallery Image Upload Dialog */}
      <Dialog open={galleryDialogOpen} onClose={() => setGalleryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add Gallery Photo</Typography>
            <IconButton onClick={() => setGalleryDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {galleryImagePreview ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img src={galleryImagePreview} alt="Preview" style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 8 }} />
                <IconButton
                  sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white' }}
                  onClick={() => {
                    setGalleryImage(null);
                    setGalleryImagePreview(null);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: 200, height: 200, bgcolor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
                <AddPhotoIcon sx={{ fontSize: 50, color: '#ccc' }} />
              </Box>
            )}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2 }}
            >
              Choose Image
              <input type="file" hidden accept="image/*" onChange={handleGalleryImageChange} />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Square image recommended, max 5MB. JPG, PNG or GIF
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGalleryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={uploadGalleryImage}
            variant="contained"
            disabled={!galleryImage || uploadingGallery}
            sx={{ background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)' }}
          >
            {uploadingGallery ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<BlockIcon />}
          onClick={async () => {
            try {
              await userAPI.blockUser(id, true);
              toast.success('User blocked successfully');
              fetchUserDetails();
            } catch (error) {
              toast.error('Failed to block user');
            }
          }}
        >
          Block User
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={async () => {
            if (window.confirm('Are you sure you want to delete this user?')) {
              try {
                await userAPI.deleteUser(id);
                toast.success('User deleted successfully');
                navigate('/users');
              } catch (error) {
                toast.error('Failed to delete user');
              }
            }
          }}
          sx={{
            background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
            }
          }}
        >
          Delete User
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetails;